"use client";

import { useSearchParams } from "next/navigation";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState, useMemo } from "react";
import { Category } from "@/packages/types/category";
import { useFastRouter } from "@/packages/hooks/useFastRouter";

interface Props {
  category: Category | undefined;
}

const CategoryFilter: React.FC<Props> = ({ category }) => {
  const [isOpen, setIsOpen] = useState(true);
  const searchParams = useSearchParams();
  const { fastPush } = useFastRouter();

  // Read selected categories from URL as a single comma-separated string
  const selectedCategories = useMemo(() => {
    const cats = searchParams.get("categories"); // changed here
    return cats ? cats.split(",").map((c) => Number(c)) : [];
  }, [searchParams]);

  // If no child categories exist, don't render this filter at all
  const childList = category?.data?.children ?? [];
  if (childList.length === 0) return null;

  const handleChange = (id: number) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));

    const updated = selectedCategories.includes(id)
      ? selectedCategories.filter((c) => c !== id)
      : [...selectedCategories, id];

    if (updated.length > 0) {
      params.set("categories", updated.join(",")); // changed here
    } else {
      params.delete("categories"); // changed here
    }

    // Reset to first page whenever filters change
    params.set("page", "1");

    fastPush(`?${params.toString()}`);
  };

  return (
    <div>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="font-[500] text-md">Category</h3>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>

      {isOpen && (
        <div className="mt-2 space-y-4 pt-4">
          {childList.map((child) => (
            <div
              key={child.id}
              className="flex items-center font-[400] space-x-2"
            >
              <input
                className="h-4 w-4 cursor-pointer"
                type="checkbox"
                id={`cat-${child.id}`}
                checked={selectedCategories.includes(child.id)}
                onChange={() => handleChange(child.id)}
              />
              <label
                htmlFor={`cat-${child.id}`}
                className="text-xs cursor-pointer"
              >
                {child.name}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryFilter;
